import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Collapse,
  Descriptions,
  Icon,
  Progress,
  Space,
  Tag,
  Text,
  Textarea,
} from "@app/ui";
import { api } from "../api/client.js";
import { RequirePermission } from "../components/RequirePermission.js";
import { useAuth } from "../auth/AuthContext.js";
import { useT } from "../i18n.js";
import { ErrorAlert } from "../errors/index.js";
import type { LicenseStatusFull } from "../types.js";

const stateColor = (s: string) => (s === "active" ? "success" : s === "grace" ? "warning" : "error");

export function LicensePage() {
  const { refreshLicense } = useAuth();
  const t = useT();
  const [status, setStatus] = useState<LicenseStatusFull | null>(null);
  const [token, setToken] = useState("");
  const [msg, setMsg] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);

  const load = async () => {
    try {
      setStatus(await api<LicenseStatusFull>("/admin/license"));
    } catch (e) {
      setError(e);
    }
  };
  useEffect(() => void load(), []);

  const upload = async () => {
    setMsg(null);
    setError(null);
    try {
      await api<LicenseStatusFull>("/admin/license", { method: "POST", body: { token: token.trim() } });
      setToken("");
      setMsg(t("license.installed"));
      await load();
      await refreshLicense();
    } catch (e) {
      setError(e);
    }
  };

  const seatPct =
    status && status.seats != null && status.seats > 0
      ? Math.round(((status.seatsUsed ?? 0) / status.seats) * 100)
      : null;

  return (
    <Space direction="vertical" size="lg" style={{ width: "100%" }}>
      <Card title={t("license.title")}>
        {error != null && <ErrorAlert error={error} onRetry={() => void load()} />}
        {!status ? (
          <Text secondary>{t("common.loading")}</Text>
        ) : (
          <>
            <Descriptions
              column={1}
              size="small"
              bordered
              items={[
                {
                  key: "state",
                  label: t("license.state"),
                  children: (
                    <>
                      <Tag color={stateColor(status.state)}>{status.state}</Tag>{" "}
                      {status.writable ? "" : t("license.readOnly")}
                    </>
                  ),
                },
                { key: "customer", label: t("license.customer"), children: status.customer ?? "—" },
                { key: "edition", label: t("license.edition"), children: status.edition ?? "—" },
                {
                  key: "expires",
                  label: t("license.expires"),
                  children: (
                    <>
                      {status.expiresAt ? new Date(status.expiresAt).toLocaleDateString() : "—"} (
                      {status.daysRemaining ?? "—"} {t("license.days")})
                    </>
                  ),
                },
                {
                  key: "features",
                  label: t("license.features"),
                  children: status.features.join(", ") || "—",
                },
              ]}
            />

            <div style={{ margin: "var(--az-sp-3) 0" }}>
              <Text small secondary>
                {t("license.seats")}: {status.seatsUsed ?? 0} / {status.seats ?? "∞"}
              </Text>
              {seatPct != null && (
                <Progress percent={seatPct} status={seatPct >= 90 ? "error" : "normal"} showInfo={false} />
              )}
            </div>

            <Collapse
              items={[
                {
                  key: "adv",
                  label: t("license.advanced"),
                  children: (
                    <Descriptions
                      column={1}
                      size="small"
                      bordered
                      items={[
                        {
                          key: "fingerprint",
                          label: t("license.fingerprint"),
                          children: (
                            <Text code copyable small style={{ wordBreak: "break-all" }}>
                              {status.fingerprint}
                            </Text>
                          ),
                        },
                        {
                          key: "reasons",
                          label: t("license.reasons"),
                          children: status.reasons.length ? status.reasons.join(" ") : "—",
                        },
                      ]}
                    />
                  ),
                },
              ]}
            />
          </>
        )}
      </Card>

      <RequirePermission permission="system:admin">
        <Card title={t("license.installTitle")}>
          {msg && <Alert type="success">{msg}</Alert>}
          <Text secondary small>
            {t("license.installHint")}
          </Text>
          <Textarea
            rows={5}
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder="eyJhbGciOiJFZERTQSJ9...."
            style={{ fontFamily: "var(--az-font-mono)", fontSize: 12 }}
          />
          <Button variant="primary" icon={<Icon name="upload" />} onClick={upload} disabled={!token.trim()}>
            {t("license.install")}
          </Button>
        </Card>
      </RequirePermission>
    </Space>
  );
}
